import dayjs, { Dayjs } from "dayjs";
import { GeoJSONCollection, GeoJSONFeature } from "../../services/pipeline";

export type MapDateRange = [Dayjs | null, Dayjs | null] | null;

export const TRANSECT_COLORS = [
  "#000080",
  "#00ffff",
  "#00ff00",
  "#adff2f",
  "#ffff00",
  "#ffa500",
  "#ff0000",
  "#8b0000",
];

const parseAcousticDate = (value: string) => dayjs(value.replace(/\//g, "-"));

export const getAvailableDateRange = (
  features: GeoJSONFeature[],
): [string | null, string | null] => {
  const dates = features
    .map((feature) => feature.properties.fecha)
    .filter(Boolean)
    .sort();
  return dates.length > 0 ? [dates[0], dates[dates.length - 1]] : [null, null];
};

export const filterFeaturesByDateRange = (
  features: GeoJSONFeature[],
  dateRange: MapDateRange,
) => {
  if (!dateRange || (!dateRange[0] && !dateRange[1])) return features;

  const [start, end] = dateRange;
  return features.filter((feature) => {
    const value = feature.properties.fecha;
    if (!value) return true;
    const featureDate = parseAcousticDate(value);
    if (!featureDate.isValid()) return true;
    if (start && featureDate.isBefore(start, "day")) return false;
    if (end && featureDate.isAfter(end, "day")) return false;
    return true;
  });
};

export const calculateMapStats = (features: GeoJSONFeature[]) => ({
  total: features.length,
  biomasa: Math.round(
    features.reduce(
      (total, feature) => total + (Number(feature.properties.biomasa) || 0),
      0,
    ),
  ),
});

export const getBiomassRange = (features: GeoJSONFeature[]): [number, number] => {
  const values = features
    .map((feature) => Number(feature.properties.biomasa))
    .filter(Number.isFinite);
  if (values.length === 0) return [0, 1];
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  return maximum > minimum ? [minimum, maximum] : [minimum, minimum + 1];
};

export const buildTransectColorExpression = (
  minimum: number,
  maximum: number,
) => {
  const span = Math.max(maximum - minimum, 1);
  const expression: Array<string | number | unknown[]> = [
    "interpolate",
    ["linear"],
    ["to-number", ["get", "biomasa"]],
  ];
  TRANSECT_COLORS.forEach((color, index) => {
    const offset = index / (TRANSECT_COLORS.length - 1);
    expression.push(minimum + span * offset, color);
  });
  return expression;
};

export const buildZoomRadiusExpression = () => [
  "interpolate",
  ["linear"],
  ["zoom"],
  3, 1.5,
  7, 2.5,
  10, 3.5,
  13, 5,
  18, 7,
];

export const buildZoomStrokeExpression = () => [
  "interpolate",
  ["linear"],
  ["zoom"],
  3, 0.2,
  10, 0.5,
  18, 1,
];

export const buildLegendTicks = (
  minimum: number,
  maximum: number,
  count = TRANSECT_COLORS.length,
) => {
  const span = Math.max(maximum - minimum, 1);
  return Array.from(
    { length: count },
    (_, index) => minimum + span * (index / (count - 1)),
  );
};

export const describeTransectValue = (
  value: number,
  minimum: number,
  maximum: number,
) => {
  const ticks = buildLegendTicks(minimum, maximum);
  let index = ticks.findIndex((tick) => value <= tick);
  if (index < 0) index = ticks.length - 1;
  const lower = index === 0 ? minimum : ticks[index - 1];
  const upper = ticks[index];
  return {
    color: TRANSECT_COLORS[index],
    label: `${Math.round(lower).toLocaleString("es-PE")} - ${Math.round(upper).toLocaleString("es-PE")}`,
  };
};

export const withFilteredFeatures = (
  collection: GeoJSONCollection,
  dateRange: MapDateRange,
): GeoJSONCollection => ({
  ...collection,
  features: filterFeaturesByDateRange(collection.features, dateRange),
});
